import React, { Fragment } from 'react'
import PropTypes from 'prop-types'
import styled, { css } from 'styled-components'

import { colours, SPACING, mobileCSS, sizes } from '@qflow/theme'

import { ValueAdd } from 'portal/components/RecordDetails/SharedComponents/ValueAdd'
import { ColourButton, Flex, icons, Text, Spacer } from 'portal/lib/primitives'
import { useInfractionGetter } from 'portal/store/infractions/InfractionsContext'
import { FormInfraction } from './FormInfraction'

const DetailContainer = styled(Flex.Row)`
  margin: ${SPACING.TINY};
  ${({ $hidden }) => $hidden && `display: none;`}
  ${mobileCSS(css`
    flex-direction: column;
  `)}
  align-items: baseline;
`
const LabelWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding-right: 0.25rem;
  height: 2rem;
  width: 15rem;
  overflow: hidden;
`

const ContentWrapper = styled(Flex.Row)`
  flex: 1 1 auto;

  flex-direction: column;
  max-width: 30rem;
`

export function FormItem({
  label,
  labelStyle = {},
  valueAdd = null,
  specialAction = null,
  children,
  hidden = false
}) {
  // Hard code the value for ValueAdd or weird things happen as the user edits the form
  const valueAddDetails = { value: 'hard-code', ...valueAdd }

  return (
    <DetailContainer hidden={hidden}>
      <LabelWrapper style={labelStyle}>
        <LabelText>{label}</LabelText>
        {specialAction && (
          <SpecialActionContainer>{specialAction}</SpecialActionContainer>
        )}
        {!specialAction && valueAdd && <ValueAdd details={valueAddDetails} />}
      </LabelWrapper>

      <ContentWrapper>{children}</ContentWrapper>
    </DetailContainer>
  )
}

FormItem.propTypes = {
  label: PropTypes.any,
  valueAdd: PropTypes.object,
  children: PropTypes.node,
  hidden: PropTypes.bool
}

/**
 * @callback renderCallback
 * @param {Partial<import("react-hook-form").ArrayField<Record<string, any>, "id">>} field
 * @param {number} index
 * @param {import("react-hook-form").UseFieldArrayMethods<Record<string, any>, "id">} fieldArray
 * @returns {any}
 *
 * @param {{
 *   fieldArray: import("react-hook-form").UseFieldArrayMethods<Record<string, any>, "id">,
 *   render: renderCallback
 * }} param0
 */
export function FormArrayItem({
  label,
  name,
  fieldArray,
  render,
  defaultValue = {},
  maxItems = 10
}) {
  const getInfraction = useInfractionGetter()
  const infraction = getInfraction(name)

  return (
    <FormItem label={label}>
      <Flex.Col>
        {infraction && (
          <ErrorRow verticalCentre>
            <FormInfraction
              infraction={
                Array.isArray(infraction) ? infraction[0] : infraction
              }
            />
          </ErrorRow>
        )}

        {fieldArray.fields.map((field, index) => {
          return (
            <Fragment key={field.id}>
              {render(field, index, fieldArray)}
            </Fragment>
          )
        })}

        {fieldArray.fields.length < maxItems && (
          <Flex.Row>
            <ColourButton.Basic
              type="button"
              onClick={() => {
                fieldArray.append(defaultValue, true)
              }}
            >
              <icons.Plus size={sizes.SMALL} />
              <Spacer.Tiny />
              Add
            </ColourButton.Basic>
          </Flex.Row>
        )}
      </Flex.Col>
    </FormItem>
  )
}

FormArrayItem.propTypes = {
  fieldArray: PropTypes.object.isRequired,
  render: PropTypes.func.isRequired,
  defaultValue: PropTypes.object.isRequired,
  label: PropTypes.string.isRequired,
  name: PropTypes.string.isRequired
}

const LabelText = styled(Text)`
  color: ${colours.CORE_NAVY};
  font-size: ${sizes.SMALL_MEDIUM};
  font-weight: 500;
`
const SpecialActionContainer = styled.div`
  width: 28px;
  height: 28px;
`
const ErrorRow = styled(Flex.Row)`
  height: 2rem;
`
